import { recordedBooleanLabel } from '@/utils/recordedValues'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CANONICAL_ROLES, getRoleLabel, normalizeRole } from '@/security/accessContract'
import { platformApi, type Page, type PlatformUser } from '@/services/platform/platform.http'

interface TenantUsersSectionProps {
  tenantId: string
}

export default function TenantUsersSection({ tenantId }: TenantUsersSectionProps) {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [role, setRole] = useState('')
  const [sort, setSort] = useState('full_name:asc')
  const [page, setPage] = useState(1)
  const [result, setResult] = useState<Page<PlatformUser> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    setResult(null)
    // Debounce search and ignore older requests when filters change.
    const timer = window.setTimeout(() => {
      const [sort_by, sort_order] = sort.split(':')
      platformApi
        .listUsers({
          tenant_id: tenantId,
          page,
          page_size: 20,
          search: search.trim() || undefined,
          is_active: status ? status === 'active' : undefined,
          role: role || undefined,
          sort_by,
          sort_order,
        })
        .then(value => {
          if (!cancelled) setResult(value)
        })
        .catch(value => {
          if (!cancelled)
            setError(value instanceof Error ? value.message : 'Unable to load tenant users')
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [tenantId, page, search, status, role, sort, retry])

  return (
    <section className="card card-elevated p-6 space-y-4 lg:col-span-2">
      <h2 className="text-lg font-semibold text-text-primary">Tenant users</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <label className="text-sm text-text-primary">
          Search users
          <input
            className="input input-bordered mt-1 w-full"
            value={search}
            placeholder="Name, email or username"
            onChange={event => {
              setSearch(event.target.value)
              setPage(1)
            }}
          />
        </label>
        <label className="text-sm text-text-primary">
          Status
          <select
            className="input input-bordered mt-1 w-full"
            value={status}
            onChange={event => {
              setStatus(event.target.value)
              setPage(1)
            }}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </label>
        <label className="text-sm text-text-primary">
          Role
          <select
            className="input input-bordered mt-1 w-full"
            value={role}
            onChange={event => {
              setRole(event.target.value)
              setPage(1)
            }}
          >
            <option value="">All roles</option>
            {CANONICAL_ROLES.filter(value => !value.startsWith('cml_')).map(value => (
              <option key={value} value={value}>
                {getRoleLabel(value)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-text-primary">
          Sort users
          <select
            className="input input-bordered mt-1 w-full"
            value={sort}
            onChange={event => {
              setSort(event.target.value)
              setPage(1)
            }}
          >
            <option value="full_name:asc">Name: A to Z</option>
            <option value="full_name:desc">Name: Z to A</option>
            <option value="username:asc">Username: A to Z</option>
            <option value="email:asc">Email: A to Z</option>
            <option value="created_at:desc">Newest first</option>
            <option value="created_at:asc">Oldest first</option>
          </select>
        </label>
      </div>
      {loading && (
        <p role="status" className="text-sm text-text-secondary">
          Loading tenant users…
        </p>
      )}
      {error && (
        <div role="alert" className="text-sm text-red-700 dark:text-red-400">
          {error}{' '}
          <button className="btn btn-outline btn-sm" onClick={() => setRetry(value => value + 1)}>
            Retry
          </button>
        </div>
      )}
      {result && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-overlay">
                <tr>
                  {['User', 'Username', 'Email', 'Role', 'Status'].map(value => (
                    <th key={value} className="px-4 py-3 text-left font-semibold text-text-primary">
                      {value}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-overlay">
                {result.items.map(user => (
                  <tr key={user.id} className="hover:bg-surface-overlay/50 transition-colors">
                    <td className="px-4 py-3">
                      <Link className="link font-medium" to={`/platform/users/${user.id}`}>
                        {user.full_name || user.username || user.email}
                      </Link>
                    </td>
                    <td className="px-4 py-3">{user.username ?? '—'}</td>
                    <td className="px-4 py-3">{user.email ?? '—'}</td>
                    <td className="px-4 py-3">
                      {user.roles.map(role => getRoleLabel(normalizeRole(role))).join(', ') || '—'}
                    </td>
                    <td className="px-4 py-3">
                      {recordedBooleanLabel(user.is_active, 'Active', 'Inactive')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {result.items.length === 0 && (
            <p className="text-sm text-text-secondary">
              No tenant users found. Try adjusting your search or filters.
            </p>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
            <p className="text-text-secondary">
              {result.total} users · Page {page} of{' '}
              {Math.max(1, result.pages ?? Math.ceil(result.total / 20))}
            </p>
            <div className="flex gap-2">
              <button
                className="btn btn-outline btn-sm"
                disabled={page === 1}
                onClick={() => setPage(value => value - 1)}
              >
                Previous
              </button>
              <button
                className="btn btn-outline btn-sm"
                disabled={page * 20 >= result.total}
                onClick={() => setPage(value => value + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  )
}
